import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { SUB_TOURS, getOrderedSubTours } from './subTourConfig';

const SubTourContext = createContext(null);

export const SubTourProvider = ({ children }) => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeSubTour, setActiveSubTour] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [completedSubTours, setCompletedSubTours] = useState({});

  // Get available sub-tours based on user role and industry
  const isAdmin = user?.role === 'admin';
  const industry = user?.industry || 'crane_hire';
  const availableSubTours = getOrderedSubTours(isAdmin, industry);

  // Fetch completed sub-tours from backend on mount
  useEffect(() => {
    const fetchOnboardingState = async () => {
      if (!api) return;
      
      try {
        const response = await api.get('/users/me/onboarding');
        if (response.data?.sub_tours_completed) {
          setCompletedSubTours(response.data.sub_tours_completed);
        }
      } catch (error) {
        // Use localStorage fallback
        const stored = localStorage.getItem('equipr_sub_tours_completed');
        if (stored) {
          try {
            setCompletedSubTours(JSON.parse(stored));
          } catch (e) {
            // Invalid JSON
          }
        }
      }
    };

    fetchOnboardingState();
  }, [api]);

  // Save completed sub-tours
  const saveCompletedSubTours = useCallback(async (newCompleted) => {
    setCompletedSubTours(newCompleted);
    localStorage.setItem('equipr_sub_tours_completed', JSON.stringify(newCompleted));
    
    if (api) {
      try {
        await api.patch('/users/me/onboarding', {
          sub_tours_completed: newCompleted
        });
      } catch (error) {
        console.log('Could not save sub-tour state to backend:', error);
      }
    }
  }, [api]);

  // Get current step based on selected choice (filters steps by forChoice)
  const getCurrentStep = useCallback(() => {
    if (!activeSubTour) return null;
    
    const tour = SUB_TOURS[activeSubTour];
    if (!tour) return null;

    // Filter steps based on selected choice
    const applicableSteps = tour.steps.filter(step => {
      if (!step.forChoice) return true; // Step applies to all paths
      return step.forChoice === selectedChoice;
    });

    return applicableSteps[currentStepIndex] || null;
  }, [activeSubTour, currentStepIndex, selectedChoice]);

  // Get total steps for current path
  const getTotalSteps = useCallback(() => {
    if (!activeSubTour) return 0;
    
    const tour = SUB_TOURS[activeSubTour];
    if (!tour) return 0;

    return tour.steps.filter(step => {
      if (!step.forChoice) return true;
      return step.forChoice === selectedChoice;
    }).length;
  }, [activeSubTour, selectedChoice]);

  // Start a sub-tour
  const startSubTour = useCallback((tourId) => {
    const tour = SUB_TOURS[tourId];
    if (!tour) return;

    setActiveSubTour(tourId);
    setCurrentStepIndex(0);
    setSelectedChoice(null);
    setIsPaused(false);
  }, []);

  // Handle choice selection (for tours with branching paths)
  const selectChoice = useCallback((choiceId) => {
    setSelectedChoice(choiceId);
    setCurrentStepIndex(1); // Move past the intro step
  }, []);

  // Next step
  const nextStep = useCallback(() => {
    const totalSteps = getTotalSteps();
    
    if (currentStepIndex < totalSteps - 1) {
      const nextStepData = getCurrentStep();
      
      // Handle navigation steps
      if (nextStepData?.type === 'navigate' && nextStepData.path) {
        navigate(nextStepData.path);
      }
      
      setCurrentStepIndex(prev => prev + 1);
      setIsPaused(false);
    }
  }, [currentStepIndex, getTotalSteps, getCurrentStep, navigate]);

  // Previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setIsPaused(false);
    }
  }, [currentStepIndex]);

  // Pause tour (waiting for user input)
  const pauseTour = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Resume tour
  const resumeTour = useCallback(() => {
    setIsPaused(false);
    nextStep();
  }, [nextStep]);

  // Exit/cancel tour
  const exitTour = useCallback(() => {
    setActiveSubTour(null);
    setCurrentStepIndex(0);
    setSelectedChoice(null);
    setIsPaused(false);
  }, []);

  // Complete tour
  const completeTour = useCallback(() => {
    if (activeSubTour) {
      const newCompleted = { ...completedSubTours, [activeSubTour]: true };
      saveCompletedSubTours(newCompleted);
      
      // Check if there's a next tour suggestion
      const currentStep = getCurrentStep();
      const nextTourId = currentStep?.nextTour;
      
      exitTour();
      
      // Return the next tour ID for the UI to potentially offer
      return nextTourId;
    }
    return null;
  }, [activeSubTour, completedSubTours, saveCompletedSubTours, getCurrentStep, exitTour]);

  // Start next recommended tour
  const startNextTour = useCallback((nextTourId) => {
    if (nextTourId && SUB_TOURS[nextTourId]) {
      startSubTour(nextTourId);
    }
  }, [startSubTour]);

  // Check if a sub-tour is completed
  const isSubTourCompleted = useCallback((tourId) => {
    return completedSubTours[tourId] === true;
  }, [completedSubTours]);

  // Get completion percentage
  const getCompletionPercentage = useCallback(() => {
    const total = availableSubTours.length;
    if (total === 0) return 0;
    
    const completed = availableSubTours.filter(t => completedSubTours[t.id]).length;
    return Math.round((completed / total) * 100);
  }, [availableSubTours, completedSubTours]);

  const currentStep = getCurrentStep();
  const totalSteps = getTotalSteps();
  const currentTour = activeSubTour ? SUB_TOURS[activeSubTour] : null;

  return (
    <SubTourContext.Provider value={{
      // State
      activeSubTour,
      currentStep,
      currentStepIndex,
      totalSteps,
      currentTour,
      selectedChoice,
      isPaused,
      completedSubTours,
      availableSubTours,
      
      // Actions
      startSubTour,
      selectChoice,
      nextStep,
      prevStep,
      pauseTour,
      resumeTour,
      exitTour,
      completeTour,
      startNextTour,
      
      // Helpers
      isSubTourCompleted,
      getCompletionPercentage
    }}>
      {children}
    </SubTourContext.Provider>
  );
};

export const useSubTour = () => {
  const context = useContext(SubTourContext);
  if (!context) {
    throw new Error('useSubTour must be used within a SubTourProvider');
  }
  return context;
};

export default SubTourContext;
